import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { getDashboard } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Briefcase,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Calendar,
  Bell
} from 'lucide-react';

const ClientDashboard = () => {
  const { t, locale } = useLanguage();
  const statusLabels = {
    planned: t('portalUi.status.planned', 'Plānots'),
    in_progress: t('portalUi.status.inProgress', 'Procesā'),
    completed: t('portalUi.status.completed', 'Pabeigts')
  };

  const statusColors = {
    planned: 'bg-blue-100 text-blue-700 border-blue-200',
    in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
    completed: 'bg-green-100 text-green-700 border-green-200'
  };

  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="client-dashboard-title">
          {t('portalUi.client.dashboard.greeting', 'Sveicināti')}, <span className="gradient-text">{user?.client_name}</span>!
        </h1>
        <p className="text-[#2E2E2E]/60 mt-1" data-testid="client-dashboard-subtitle">
          {t('portalUi.client.dashboard.subtitle', 'Jūsu')} <span className="text-[#1F3D2B] font-semibold">{t('portalUi.client.dashboard.subtitleHighlight', 'klientu portāla pārskats')}</span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="client-dashboard-total-jobs-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.dashboard.stats.totalJobs', 'Kopā darbu')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E] mt-1" data-testid="client-dashboard-total-jobs-value">{dashboard?.total_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#1F3D2B]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="client-dashboard-active-jobs-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.dashboard.stats.activeJobs', 'Aktīvie darbi')}</p>
                <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="client-dashboard-active-jobs-value">{dashboard?.active_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="client-dashboard-completed-jobs-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.dashboard.stats.completedJobs', 'Pabeigti')}</p>
                <p className="text-3xl font-bold text-green-600 mt-1" data-testid="client-dashboard-completed-jobs-value">{dashboard?.completed_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all" data-testid="client-dashboard-messages-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.dashboard.stats.newMessages', 'Jauni ziņojumi')}</p>
                <p className="text-3xl font-bold text-blue-600 mt-1" data-testid="client-dashboard-messages-value">{dashboard?.unread_messages || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last Update */}
      {dashboard?.last_update && (
        <div className="flex items-center gap-2 text-sm text-[#2E2E2E]/60" data-testid="client-dashboard-last-update">
          <Calendar className="w-4 h-4" />
          <span data-testid="client-dashboard-last-update-value">
            {t('portalUi.client.dashboard.lastUpdate', 'Pēdējais atjauninājums:')} {new Date(dashboard.last_update).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/portal/jobs" data-testid="client-dashboard-jobs-link">
          <Card className="border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 hover-lift card-shine transition-all cursor-pointer group">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center group-hover:bg-[#1F3D2B] transition-colors">
                  <Briefcase className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2E2E2E]">{t('portalUi.client.dashboard.actions.jobsTitle', 'Skatīt visus darbus')}</h3>
                  <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.dashboard.actions.jobsDescription', 'Pārskatīt darbu statusus un detaļas')}</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#2E2E2E]/40 group-hover:text-[#1F3D2B] group-hover:translate-x-1 transition-all" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/portal/documents" data-testid="client-dashboard-documents-link">
          <Card className="border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 hover-lift card-shine transition-all cursor-pointer group">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center group-hover:bg-[#1F3D2B] transition-colors">
                  <FileText className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2E2E2E]">{t('portalUi.client.dashboard.actions.documentsTitle', 'Dokumenti')}</h3>
                  <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.dashboard.actions.documentsDescription', 'Apskatīt un lejuplādēt failus')}</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#2E2E2E]/40 group-hover:text-[#1F3D2B] group-hover:translate-x-1 transition-all" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Jobs */}
      <Card className="border-0 shadow-sm card-shine" data-testid="client-dashboard-recent-jobs">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('portalUi.client.dashboard.recentTitle', 'Pēdējie darbi')}</CardTitle>
            <Link to="/portal/jobs" className="text-sm text-[#1F3D2B] hover:underline font-medium" data-testid="client-dashboard-recent-jobs-link">
              {t('portalUi.client.dashboard.viewAll', 'Skatīt visus')}
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {dashboard?.recent_jobs?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_jobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/portal/jobs/${job.id}`}
                  className="flex items-center justify-between p-4 rounded-xl bg-[#f8faf8] hover:bg-[#1F3D2B]/5 transition-colors group hover-lift"
                  data-testid={`client-dashboard-recent-job-${job.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      job.status === 'completed' ? 'bg-green-500' :
                      job.status === 'in_progress' ? 'bg-amber-500' : 'bg-blue-500'
                    }`} />
                    <div>
                      <p className="font-medium text-[#2E2E2E] group-hover:text-[#1F3D2B] transition-colors">
                        {job.title}
                      </p>
                      {job.responsible_person && (
                        <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.dashboard.responsibleLabel', 'Atbildīgais')}: {job.responsible_person}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[job.status]}`}>
                    {statusLabels[job.status]}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#2E2E2E]/60">
              <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('portalUi.client.dashboard.emptyJobs', 'Vēl nav darbu')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDashboard;
